import { Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { SystemAutoCompleteOption } from "../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import type { PbjShiftManualEditBody, PbjStaffingHoursRow, PbjStaffingShiftRow } from "../../../utils/models";
import { useEditPbjStaffingShift, usePbjStaffingColumnVisibility, usePbjStaffingShifts } from "../hooks/usePbjAudit";
import { DstBadge } from "./DstBadge";
import { EditableHoverCell } from "./EditableHoverCell";
import { JobSelectEditCell, PayTypeOverrideBadge, RoleStringEditCell, usePayTypeCodeOptions } from "./staffingEditCells";

type Props = {
	row: PbjStaffingHoursRow;
	departmentId: string;
	// All three include the leading clear ("—") option; jobOptions is labelled by description,
	// codeOptions by pay-title code, roleStringOptions are the dept's known raw role strings.
	jobOptions: SystemAutoCompleteOption<string>[];
	codeOptions: SystemAutoCompleteOption<string>[];
	roleStringOptions: string[];
};

const HOURS_MIN_WIDTH = 90;

/**
 * DAT-209 row-expand sub-grid for a non-inline-editable Staffing-Hours group (multi-shift or
 * midnight-split). Lazily fetches the underlying pbj_shift rows and renders a minimal grid;
 * each row's metadata/job fields PATCH via the same mutation as the inline editor. Hours are read-only.
 */
export const StaffingHoursExpand: FC<Props> = ({ row, departmentId, jobOptions, codeOptions, roleStringOptions }) => {
	const { t } = useTranslation();
	const { data, isLoading, isError } = usePbjStaffingShifts({ pbjEmployeeId: row.empId, date: row.date, pbjJobId: row.pbjJobId }, true);
	const editMutation = useEditPbjStaffingShift();
	const payTypeOptions = usePayTypeCodeOptions();
	// Empeon-only depts never populate EarnCode, so hide that column here too (matches the parent table).
	const { data: columnVisibility } = usePbjStaffingColumnVisibility();
	const hideSourceSpecificColumns = columnVisibility?.hideSourceSpecificColumns ?? false;

	const processRowUpdate = useCallback(
		async (newRow: PbjStaffingShiftRow, oldRow: PbjStaffingShiftRow): Promise<PbjStaffingShiftRow> => {
			const body: PbjShiftManualEditBody = { departmentId };
			let changedField = "";
			if (newRow.pbjJobId !== oldRow.pbjJobId) {
				body.pbjJobId = newRow.pbjJobId || null;
				changedField = "pbjJobId";
			} else if (newRow.rawJob !== oldRow.rawJob) {
				body.rawJob = newRow.rawJob || null;
				changedField = "rawJob";
			} else if (newRow.departmentName !== oldRow.departmentName) {
				body.departmentName = newRow.departmentName || null;
				changedField = "departmentName";
			} else if (newRow.earnCode !== oldRow.earnCode) {
				body.earnCode = newRow.earnCode || null;
				changedField = "earnCode";
			} else if (newRow.agencyName !== oldRow.agencyName) {
				body.agencyName = newRow.agencyName || null;
				changedField = "agencyName";
			} else if (newRow.payTypeCode !== oldRow.payTypeCode) {
				// "1"|"2"|"3" sets a per-shift manual override; null clears it (revert to the derived code).
				body.payTypeCode = newRow.payTypeCode ?? null;
				changedField = "payTypeCode";
			} else {
				return newRow;
			}
			// Await the PATCH so a rejection propagates to onProcessRowUpdateError and the sub-grid reverts
			// the cell. On success the open sub-grid is invalidated/refetched; the mutation's onError toasts.
			await editMutation.mutateAsync({ pbjShiftId: newRow.pbjShiftId, body, changedField });
			return newRow;
		},
		[departmentId, editMutation],
	);

	// The mutation already toasts + tracks the failure; this lets the sub-grid revert the edited
	// cell to its prior value instead of leaving the bad edit on screen (matches StaffingHoursTable).
	const handleProcessRowUpdateError = useCallback((error: unknown) => {
		console.error("Staffing Hours shift-detail cell edit failed; reverting cell", error);
	}, []);

	const columns: GridColDef<PbjStaffingShiftRow>[] = useMemo(
		() => [
			{
				field: "startTime",
				headerName: t("pbj_model.staffing.edit.col.shift_window"),
				minWidth: 170,
				flex: 1,
				valueGetter: (_value, r) => `${DateTime.fromISO(r.startTime).toFormat("M/d HH:mm")} – ${DateTime.fromISO(r.endTime).toFormat("HH:mm")}`,
				// Mirror the parent grid's DST marker per contributing shift, so a reviewer can attribute a
				// DST-affected employee-day to the exact shift that crossed the transition. Hours stay read-only.
				renderCell: (params: GridRenderCellParams<PbjStaffingShiftRow>) => {
					if (!params.row.dstAffected) return params.value;
					return <DstBadge label={params.value} dataTestId={`pbj-staffing-dst-badge-shift-${params.row.pbjShiftId}`} />;
				},
			},
			{
				field: "hours",
				headerName: t("pbj_model.staffing.col.hours"),
				minWidth: HOURS_MIN_WIDTH,
				type: "number",
				// Numeric sort, left-aligned to match the other columns.
				align: "left",
				headerAlign: "left",
				valueFormatter: (value: number) => (value == null ? "" : value.toFixed(2)),
			},
			{
				field: "breakHours",
				headerName: t("pbj_model.staffing.col.break_removed"),
				minWidth: HOURS_MIN_WIDTH,
				type: "number",
				align: "left",
				headerAlign: "left",
				// "—" when nothing was removed, so a glance distinguishes "no break" from a real deduction.
				valueFormatter: (value: number) => (value == null ? "" : value === 0 ? "—" : value.toFixed(2)),
			},
			{
				field: "rawJob",
				headerName: t("pbj_model.staffing.col.job_description"),
				minWidth: 160,
				flex: 1,
				editable: true,
				renderCell: (params) => <EditableHoverCell {...params} display={params.value ?? ""} />,
				renderEditCell: (params) => <RoleStringEditCell {...params} options={roleStringOptions} />,
			},
			{
				field: "payTitleCode",
				headerName: t("pbj_model.staffing.col.pay_title_code"),
				minWidth: 110,
				editable: true,
				// Picking a code sets the job; funnel into pbjJobId like the main table.
				valueGetter: (_value, r) => r.pbjJobId ?? "",
				valueSetter: (value, r) => ({ ...r, pbjJobId: value === "" ? null : (value as string) }),
				renderCell: (params) => <EditableHoverCell {...params} display={params.row.payTitleCode ?? ""} />,
				renderEditCell: (params) => <JobSelectEditCell {...params} options={codeOptions} />,
			},
			{
				field: "payTypeCode",
				headerName: t("pbj_model.staffing.col.pay_type_code"),
				minWidth: 110,
				editable: true,
				// Effective code as the edit value; the "—" (derived) option clears this shift's override.
				valueGetter: (_value, r) => r.payTypeCode ?? "",
				valueSetter: (value, r) => ({ ...r, payTypeCode: value === "" ? null : (value as string) }),
				renderCell: (params) => (
					<EditableHoverCell
						{...params}
						display={params.row.payTypeCode ?? ""}
						adornment={params.row.payTypeCodeOverride != null ? <PayTypeOverrideBadge /> : undefined}
					/>
				),
				renderEditCell: (params) => <JobSelectEditCell {...params} options={payTypeOptions} />,
			},
			{
				field: "pbjJobId",
				headerName: t("pbj_model.staffing.col.pbj_job_id"),
				minWidth: 200,
				flex: 1,
				editable: true,
				valueGetter: (_value, r) => r.pbjJobId ?? "",
				renderCell: (params) => <EditableHoverCell {...params} display={jobOptions.find((o) => o.value === (params.row.pbjJobId ?? ""))?.label ?? ""} />,
				renderEditCell: (params) => <JobSelectEditCell {...params} options={jobOptions} />,
			},
			{
				field: "departmentName",
				headerName: t("pbj_model.staffing.col.department_name"),
				minWidth: 150,
				flex: 1,
				editable: true,
				renderCell: (params) => <EditableHoverCell {...params} display={params.value ?? ""} />,
			},
			// EarnCode is always NULL for Empeon-only depts, so omit it there (matches the parent table).
			...(hideSourceSpecificColumns
				? []
				: [
						{
							field: "earnCode",
							headerName: t("pbj_model.staffing.col.earn_code"),
							minWidth: 100,
							editable: true,
							renderCell: (params: GridRenderCellParams<PbjStaffingShiftRow>) => <EditableHoverCell {...params} display={params.value ?? ""} />,
						},
					]),
			{
				field: "agencyName",
				headerName: t("pbj_model.staffing.col.agency_name"),
				minWidth: 140,
				flex: 1,
				editable: true,
				renderCell: (params) => <EditableHoverCell {...params} display={params.value ?? ""} />,
			},
		],
		[t, jobOptions, codeOptions, roleStringOptions, payTypeOptions, hideSourceSpecificColumns],
	);

	return (
		<SystemDataGrid<PbjStaffingShiftRow>
			rows={data ?? []}
			columns={columns}
			getRowId={(r) => r.pbjShiftId}
			processRowUpdate={processRowUpdate}
			onProcessRowUpdateError={handleProcessRowUpdateError}
			loading={isLoading}
			error={isError}
			dataTestId={`pbj-staffing-expand-${row.empId}-${row.date}`}
			dataGridProps={{ hideFooter: true, columnHeaderHeight: 36 }}
			leadingToolbarNodes={
				<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
					{t("pbj_model.staffing.edit.expand_title")}
				</Typography>
			}
		/>
	);
};
